import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import {
  buildUnsplashCredit,
  generateAltText,
  getOptimizedImageUrl,
  hasUnsplashAccessKey,
  searchUnsplashPhotos,
  type UnsplashPhoto,
} from '../services/unsplashService';
import {
  buildGlobalSymbolCredit,
  generateGlobalSymbolAltText,
  getGlobalSymbolImageUrl,
  searchGlobalSymbolsLabels,
  type GlobalSymbolsLabel,
} from '../services/globalSymbolsService';
import {
  buildArasaacCredit,
  generateArasaacAltText,
  getArasaacImageUrl,
  searchArasaacPictograms,
  type ArasaacPictogram,
} from '../services/arasaacService';
import {
  buildOpenMojiCredit,
  generateOpenMojiAltText,
  getOpenMojiPngUrl,
  searchOpenMoji,
  type OpenMojiEntry,
} from '../services/openMojiService';
import {
  buildOpenverseCredit,
  generateOpenverseAltText,
  getOpenverseImageUrl,
  getOpenverseThumbnailUrl,
  searchOpenverseImages,
  type OpenverseImage,
} from '../services/openverseService';
import {
  buildWikimediaCredit,
  generateWikimediaAltText,
  getWikimediaImageUrl,
  getWikimediaThumbnailUrl,
  searchWikimediaImages,
  type WikimediaImage,
} from '../services/wikimediaService';

import UserAvatar from '@/shared/components/UserAvatar';
import {
  uploadFile,
  extFromMime,
  resolveSignedUrl,
  PASTED_IMAGES_MAX_SIZE,
  type StorageBucket,
} from '@/shared/services/storageService';
import { resizeImageBlob } from '@/shared/utils/imageResize';

const PAGE_SIZE = 20;
type MediaSourceTab =
  | 'unsplash'
  | 'symbols'
  | 'arasaac'
  | 'openmoji'
  | 'openverse'
  | 'wikimedia'
  | 'upload';

interface UnsplashImageSelection {
  src: string;
  alt: string;
  title: string;
  storageBucket?: StorageBucket;
  storagePath?: string;
}

/** Context required for the Upload tab. When omitted the tab is hidden. */
interface UploadContext {
  userId: string;
  resourceId: string;
}

interface UnsplashImageLibraryDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectImage: (selection: UnsplashImageSelection) => void;
  initialSearchTerm?: string;
  /** When true the dialog stays open after a selection (caller manages closing). */
  keepOpenOnSelect?: boolean;
  /** Provide to enable the Upload from Computer tab. */
  uploadContext?: UploadContext;
}

const UnsplashImageLibraryDialog: React.FC<UnsplashImageLibraryDialogProps> = ({
  open,
  onClose,
  onSelectImage,
  initialSearchTerm = '',
  keepOpenOnSelect = false,
  uploadContext,
}) => {
  const unsplashConfigured = hasUnsplashAccessKey();
  const [searchTerm, setSearchTerm] = React.useState(initialSearchTerm);
  const [activeTab, setActiveTab] = React.useState<MediaSourceTab>(
    unsplashConfigured ? 'unsplash' : 'symbols'
  );
  const [photos, setPhotos] = React.useState<UnsplashPhoto[]>([]);
  const [symbols, setSymbols] = React.useState<GlobalSymbolsLabel[]>([]);
  const [arasaacIcons, setArasaacIcons] = React.useState<ArasaacPictogram[]>([]);
  const [openMojiIcons, setOpenMojiIcons] = React.useState<OpenMojiEntry[]>([]);
  const [openverseImages, setOpenverseImages] = React.useState<OpenverseImage[]>([]);
  const [wikimediaImages, setWikimediaImages] = React.useState<WikimediaImage[]>([]);
  const [unsplashLoading, setUnsplashLoading] = React.useState(false);
  const [symbolsLoading, setSymbolsLoading] = React.useState(false);
  const [arasaacLoading, setArasaacLoading] = React.useState(false);
  const [openMojiLoading, setOpenMojiLoading] = React.useState(false);
  const [openverseLoading, setOpenverseLoading] = React.useState(false);
  const [wikimediaLoading, setWikimediaLoading] = React.useState(false);
  const [unsplashError, setUnsplashError] = React.useState<string | null>(null);
  const [symbolsError, setSymbolsError] = React.useState<string | null>(null);
  const [arasaacError, setArasaacError] = React.useState<string | null>(null);
  const [openMojiError, setOpenMojiError] = React.useState<string | null>(null);
  const [openverseError, setOpenverseError] = React.useState<string | null>(null);
  const [wikimediaError, setWikimediaError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);
  const [uploadLoading, setUploadLoading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const lastInitialSearchTermRef = React.useRef<string | null>(null);

  const handleFileUpload = React.useCallback(
    async (file: File) => {
      if (!uploadContext) return;
      if (!file.type.startsWith('image/')) {
        setUploadError('Only image files are supported.');
        return;
      }
      if (file.size > PASTED_IMAGES_MAX_SIZE) {
        setUploadError(`File exceeds the ${PASTED_IMAGES_MAX_SIZE / (1024 * 1024)} MB limit.`);
        return;
      }

      setUploadLoading(true);
      setUploadError(null);

      try {
        const resized = await resizeImageBlob(file);
        const ext = extFromMime(resized.type || file.type);
        const { storageBucket, storagePath } = await uploadFile(
          'pasted-images',
          uploadContext.userId,
          uploadContext.resourceId,
          resized,
          ext
        );
        const signedUrl = await resolveSignedUrl(storageBucket, storagePath);
        const alt = file.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ');

        onSelectImage({ src: signedUrl, alt, title: '', storageBucket, storagePath });
        if (!keepOpenOnSelect) handleClose();
      } catch (err) {
        console.error('Image upload failed', err);
        setUploadError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      } finally {
        setUploadLoading(false);
      }
    },
    [uploadContext, onSelectImage, keepOpenOnSelect]
  );

  const handleDrop = React.useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragOver(false);
      const file = event.dataTransfer.files[0];
      if (file) void handleFileUpload(file);
    },
    [handleFileUpload]
  );

  const handleDragOver = React.useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = React.useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleUnsplashSearch = React.useCallback(
    async (query: string, nextPage = 1) => {
      if (!unsplashConfigured) return;
      const trimmedQuery = query.trim();
      if (!trimmedQuery) return;

      setUnsplashLoading(true);
      setUnsplashError(null);

      try {
        const results = await searchUnsplashPhotos(trimmedQuery, nextPage, PAGE_SIZE);
        if (nextPage === 1) {
          setPhotos(results);
        } else {
          setPhotos((previous) => [...previous, ...results]);
        }
        setPage(nextPage);
        setHasMore(results.length === PAGE_SIZE);
      } catch (searchError) {
        console.error('Unsplash search failed', searchError);
        setUnsplashError(
          searchError instanceof Error ? searchError.message : 'Failed to search Unsplash images.'
        );
      } finally {
        setUnsplashLoading(false);
      }
    },
    [unsplashConfigured]
  );

  const handleGlobalSymbolsSearch = React.useCallback(async (query: string) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    setSymbolsLoading(true);
    setSymbolsError(null);

    try {
      const results = await searchGlobalSymbolsLabels(trimmedQuery, { limit: 24, language: 'eng' });
      setSymbols(results.filter((item) => getGlobalSymbolImageUrl(item).length > 0));
    } catch (searchError) {
      console.error('Global Symbols search failed', searchError);
      setSymbolsError(
        searchError instanceof Error ? searchError.message : 'Failed to search Global Symbols.'
      );
    } finally {
      setSymbolsLoading(false);
    }
  }, []);

  const handleArasaacSearch = React.useCallback(async (query: string) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    setArasaacLoading(true);
    setArasaacError(null);

    try {
      const results = await searchArasaacPictograms(trimmedQuery, { limit: 30, language: 'en' });
      setArasaacIcons(results);
    } catch (searchError) {
      console.error('ARASAAC search failed', searchError);
      setArasaacError(
        searchError instanceof Error ? searchError.message : 'Failed to search ARASAAC pictograms.'
      );
    } finally {
      setArasaacLoading(false);
    }
  }, []);

  const handleOpenMojiSearch = React.useCallback(async (query: string) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    setOpenMojiLoading(true);
    setOpenMojiError(null);

    try {
      const results = await searchOpenMoji(trimmedQuery, { limit: 40 });
      setOpenMojiIcons(results);
    } catch (searchError) {
      console.error('OpenMoji search failed', searchError);
      setOpenMojiError(
        searchError instanceof Error ? searchError.message : 'Failed to search OpenMoji.'
      );
    } finally {
      setOpenMojiLoading(false);
    }
  }, []);

  const handleOpenverseSearch = React.useCallback(async (query: string) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    setOpenverseLoading(true);
    setOpenverseError(null);

    try {
      const { images } = await searchOpenverseImages(trimmedQuery, 1, PAGE_SIZE);
      setOpenverseImages(images);
    } catch (searchError) {
      console.error('Openverse search failed', searchError);
      setOpenverseError(
        searchError instanceof Error ? searchError.message : 'Failed to search Openverse images.'
      );
    } finally {
      setOpenverseLoading(false);
    }
  }, []);

  const handleWikimediaSearch = React.useCallback(async (query: string) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    setWikimediaLoading(true);
    setWikimediaError(null);

    try {
      const results = await searchWikimediaImages(trimmedQuery, 20);
      setWikimediaImages(results);
    } catch (searchError) {
      console.error('Wikimedia search failed', searchError);
      setWikimediaError(
        searchError instanceof Error ? searchError.message : 'Failed to search Wikimedia Commons.'
      );
    } finally {
      setWikimediaLoading(false);
    }
  }, []);

  const handleSearch = React.useCallback(
    (query: string) => {
      const trimmedQuery = query.trim();
      if (!trimmedQuery) return;

      if (unsplashConfigured) {
        void handleUnsplashSearch(trimmedQuery, 1);
      } else {
        setPhotos([]);
        setHasMore(false);
        setPage(1);
        setUnsplashError(null);
      }

      void handleGlobalSymbolsSearch(trimmedQuery);
      void handleArasaacSearch(trimmedQuery);
      void handleOpenMojiSearch(trimmedQuery);
      void handleOpenverseSearch(trimmedQuery);
      void handleWikimediaSearch(trimmedQuery);
    },
    [
      handleArasaacSearch,
      handleGlobalSymbolsSearch,
      handleOpenMojiSearch,
      handleOpenverseSearch,
      handleWikimediaSearch,
      handleUnsplashSearch,
      unsplashConfigured,
    ]
  );

  React.useEffect(() => {
    if (!open) return;

    const nextTerm = initialSearchTerm.trim();
    const isNewInitialTerm =
      lastInitialSearchTermRef.current === null || lastInitialSearchTermRef.current !== nextTerm;
    lastInitialSearchTermRef.current = nextTerm;

    // Always clear transient state (loading, errors, drag) on (re)open
    setUnsplashLoading(false);
    setSymbolsLoading(false);
    setArasaacLoading(false);
    setOpenMojiLoading(false);
    setOpenverseLoading(false);
    setWikimediaLoading(false);
    setUnsplashError(null);
    setSymbolsError(null);
    setArasaacError(null);
    setOpenMojiError(null);
    setOpenverseError(null);
    setWikimediaError(null);
    setUploadLoading(false);
    setUploadError(null);
    setIsDragOver(false);

    if (!unsplashConfigured) {
      setActiveTab((prev) => (prev === 'unsplash' ? 'symbols' : prev));
    }

    // Only reset the search term + results when the parent passes a new
    // initialSearchTerm. Otherwise preserve the previous search so users can
    // pick a different result without re-running the search.
    if (!isNewInitialTerm) return;

    setSearchTerm(nextTerm);
    setPhotos([]);
    setSymbols([]);
    setArasaacIcons([]);
    setOpenMojiIcons([]);
    setOpenverseImages([]);
    setWikimediaImages([]);
    setPage(1);
    setHasMore(true);

    if (nextTerm) {
      handleSearch(nextTerm);
    }
  }, [handleSearch, initialSearchTerm, open, unsplashConfigured]);

  React.useEffect(() => {
    if (!unsplashConfigured && activeTab === 'unsplash') {
      setActiveTab('symbols');
    }
  }, [activeTab, unsplashConfigured]);

  const isSearching =
    unsplashLoading ||
    symbolsLoading ||
    arasaacLoading ||
    openMojiLoading ||
    openverseLoading ||
    wikimediaLoading;

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleSearch(searchTerm);
  };

  const handleLoadMore = () => {
    if (!unsplashConfigured || unsplashLoading || !hasMore || !searchTerm.trim()) return;
    void handleUnsplashSearch(searchTerm, page + 1);
  };

  const handleSelectPhoto = (photo: UnsplashPhoto) => {
    const src = getOptimizedImageUrl(photo, 1000, 700, 'crop');
    const alt = generateAltText(photo, searchTerm);
    const title = buildUnsplashCredit(photo);

    if (!src) {
      setUnsplashError('Unable to use this image. Please pick another result.');
      return;
    }

    onSelectImage({ src, alt, title });
    if (!keepOpenOnSelect) handleClose();
  };

  const handleSelectGlobalSymbol = (label: GlobalSymbolsLabel) => {
    const src = getGlobalSymbolImageUrl(label);
    const alt = generateGlobalSymbolAltText(label, searchTerm);
    const title = buildGlobalSymbolCredit(label);

    if (!src) {
      setSymbolsError('Unable to use this symbol. Please pick another result.');
      return;
    }

    onSelectImage({ src, alt, title });
    if (!keepOpenOnSelect) handleClose();
  };

  const handleSelectArasaacIcon = (icon: ArasaacPictogram) => {
    const src = getArasaacImageUrl(icon, 300);
    const alt = generateArasaacAltText(icon, searchTerm);
    const title = buildArasaacCredit();

    if (!src) {
      setArasaacError('Unable to use this pictogram. Please pick another result.');
      return;
    }

    onSelectImage({ src, alt, title });
    if (!keepOpenOnSelect) handleClose();
  };

  const handleSelectOpenMojiIcon = (icon: OpenMojiEntry) => {
    const src = getOpenMojiPngUrl(icon);
    const alt = generateOpenMojiAltText(icon, searchTerm);
    const title = buildOpenMojiCredit();

    if (!src) {
      setOpenMojiError('Unable to use this OpenMoji icon. Please pick another result.');
      return;
    }

    onSelectImage({ src, alt, title });
    if (!keepOpenOnSelect) handleClose();
  };

  const handleSelectOpenverseImage = (image: OpenverseImage) => {
    const src = getOpenverseImageUrl(image);
    const alt = generateOpenverseAltText(image, searchTerm);
    const title = buildOpenverseCredit(image);

    if (!src) {
      setOpenverseError('Unable to use this image. Please pick another result.');
      return;
    }

    onSelectImage({ src, alt, title });
    if (!keepOpenOnSelect) handleClose();
  };

  const handleSelectWikimediaImage = (image: WikimediaImage) => {
    const src = getWikimediaImageUrl(image);
    const alt = generateWikimediaAltText(image, searchTerm);
    const title = buildWikimediaCredit(image);

    if (!src) {
      setWikimediaError('Unable to use this image. Please pick another result.');
      return;
    }

    onSelectImage({ src, alt, title });
    if (!keepOpenOnSelect) handleClose();
  };

  const handleClose = () => {
    // Preserve search term and results so reopening the dialog restores the
    // user's last search. Only clear transient state.
    setUnsplashError(null);
    setSymbolsError(null);
    setArasaacError(null);
    setOpenMojiError(null);
    setOpenverseError(null);
    setWikimediaError(null);
    setUnsplashLoading(false);
    setSymbolsLoading(false);
    setArasaacLoading(false);
    setOpenMojiLoading(false);
    setOpenverseLoading(false);
    setWikimediaLoading(false);
    setUploadLoading(false);
    setUploadError(null);
    setIsDragOver(false);
    onClose();
  };

  const activeError =
    activeTab === 'unsplash'
      ? unsplashError
      : activeTab === 'symbols'
        ? symbolsError
        : activeTab === 'arasaac'
          ? arasaacError
          : activeTab === 'openmoji'
            ? openMojiError
            : activeTab === 'openverse'
              ? openverseError
              : activeTab === 'wikimedia'
                ? wikimediaError
                : uploadError;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '80vh',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}
      >
        <Typography variant="h6">Image & Symbol Library</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {activeTab !== 'upload' && (
          <Box
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
          >
            <TextField
              fullWidth
              variant="outlined"
              sx={{
                flex: '1 1 320px',
                '& .MuiOutlinedInput-input': { py: 1.25 },
              }}
              placeholder={
                activeTab === 'unsplash'
                  ? 'Search Unsplash images...'
                  : activeTab === 'symbols'
                    ? 'Search Global Symbols...'
                    : activeTab === 'arasaac'
                      ? 'Search ARASAAC pictograms...'
                      : activeTab === 'openmoji'
                        ? 'Search OpenMoji icons...'
                        : activeTab === 'openverse'
                          ? 'Search Openverse images...'
                          : 'Search Wikimedia Commons...'
              }
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchTerm('')} size="small">
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!searchTerm.trim() || isSearching}
              startIcon={isSearching ? <CircularProgress size={16} /> : <SearchIcon />}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
          </Box>
        )}

        <Tabs
          value={activeTab}
          onChange={(_event, value) => setActiveTab(value as MediaSourceTab)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          <Tab value="unsplash" label="Unsplash Photos" disabled={!unsplashConfigured} />
          <Tab value="symbols" label="Global Symbols" />
          <Tab value="arasaac" label="ARASAAC" />
          <Tab value="openmoji" label="OpenMoji" />
          <Tab value="openverse" label="Openverse" />
          <Tab value="wikimedia" label="Wikimedia" />
          {uploadContext && (
            <Tab value="upload" label="Upload" icon={<CloudUploadIcon />} iconPosition="start" />
          )}
        </Tabs>

        {!unsplashConfigured && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Unsplash is not configured. Add `VITE_UNSPLASH_ACCESS_KEY` to your `.env.local`. Symbol
            tabs remain available.
          </Alert>
        )}

        {activeError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {activeError}
          </Alert>
        )}

        {activeTab === 'unsplash' && photos.length > 0 && (
          <Box sx={{ height: 'calc(80vh - 210px)', overflowY: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 2,
              }}
            >
              {photos.map((photo) => (
                <Card
                  key={photo.id}
                  sx={{
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3,
                    },
                  }}
                  onClick={() => handleSelectPhoto(photo)}
                >
                  <CardMedia
                    component="img"
                    height="180"
                    image={photo.urls.small}
                    alt={photo.alt_description || 'Unsplash image'}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                      <UserAvatar
                        userId={photo.user.username}
                        name={photo.user.name}
                        src={photo.user.profile_image?.small}
                        size={22}
                      />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {photo.user.name}
                      </Typography>
                    </Box>

                    {(photo.alt_description || photo.description) && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.2,
                          minHeight: 32,
                        }}
                      >
                        {photo.alt_description || photo.description}
                      </Typography>
                    )}

                    <Box
                      sx={{
                        mt: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Chip
                        label={`${photo.width} x ${photo.height}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                      <Tooltip title="Use Image">
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSelectPhoto(photo);
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {hasMore && photos.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={unsplashLoading}
                  startIcon={unsplashLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                >
                  {unsplashLoading ? 'Loading...' : 'Load More'}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {activeTab === 'symbols' && symbols.length > 0 && (
          <Box sx={{ height: 'calc(80vh - 210px)', overflowY: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 2,
              }}
            >
              {symbols.map((label) => {
                const imageUrl = getGlobalSymbolImageUrl(label);
                const symbolset = label.picto?.symbolset?.name || 'Global Symbols';
                const nativeFormat = label.picto?.native_format?.toUpperCase() || 'IMG';

                return (
                  <Card
                    key={label.id}
                    sx={{
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                      },
                    }}
                    onClick={() => handleSelectGlobalSymbol(label)}
                  >
                    <CardMedia
                      component="img"
                      height="140"
                      image={imageUrl}
                      alt={label.text || 'Global Symbols icon'}
                      sx={{ objectFit: 'contain', backgroundColor: '#fafafa', p: 1 }}
                    />
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {label.text}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {symbolset}
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Chip
                          label={nativeFormat}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                        <Tooltip title="Use Symbol">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSelectGlobalSymbol(label);
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}

        {activeTab === 'arasaac' && arasaacIcons.length > 0 && (
          <Box sx={{ height: 'calc(80vh - 210px)', overflowY: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 2,
              }}
            >
              {arasaacIcons.map((icon) => {
                const imageUrl = getArasaacImageUrl(icon, 300);
                const firstKeyword = icon.keywords
                  ?.find(
                    (entry) => typeof entry?.keyword === 'string' && entry.keyword.trim().length > 0
                  )
                  ?.keyword?.trim();
                const primaryLabel = firstKeyword || `Pictogram ${icon._id}`;

                return (
                  <Card
                    key={icon._id}
                    sx={{
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                      },
                    }}
                    onClick={() => handleSelectArasaacIcon(icon)}
                  >
                    <CardMedia
                      component="img"
                      height="140"
                      image={imageUrl}
                      alt={primaryLabel}
                      sx={{ objectFit: 'contain', backgroundColor: '#fafafa', p: 1 }}
                    />
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {primaryLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        ARASAAC
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Chip
                          label="PNG"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                        <Tooltip title="Use Pictogram">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSelectArasaacIcon(icon);
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}

        {activeTab === 'openmoji' && openMojiIcons.length > 0 && (
          <Box sx={{ height: 'calc(80vh - 210px)', overflowY: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 2,
              }}
            >
              {openMojiIcons.map((icon) => {
                const imageUrl = getOpenMojiPngUrl(icon);
                const label = icon.annotation?.trim() || icon.hexcode;

                return (
                  <Card
                    key={icon.hexcode}
                    sx={{
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                      },
                    }}
                    onClick={() => handleSelectOpenMojiIcon(icon)}
                  >
                    <CardMedia
                      component="img"
                      height="140"
                      image={imageUrl}
                      alt={label}
                      sx={{ objectFit: 'contain', backgroundColor: '#fafafa', p: 1 }}
                    />
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        OpenMoji
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Chip
                          label="PNG"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                        <Tooltip title="Use Icon">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSelectOpenMojiIcon(icon);
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}

        {activeTab === 'openverse' && openverseImages.length > 0 && (
          <Box sx={{ height: 'calc(80vh - 210px)', overflowY: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 2,
              }}
            >
              {openverseImages.map((image) => {
                const thumbnailUrl = getOpenverseThumbnailUrl(image);
                const label = image.title?.trim() || 'Untitled';
                const creator = image.creator?.trim() || 'Unknown';
                const license = image.license?.toUpperCase() || 'CC';

                return (
                  <Card
                    key={image.id}
                    sx={{
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                      },
                    }}
                    onClick={() => handleSelectOpenverseImage(image)}
                  >
                    <CardMedia
                      component="img"
                      height="180"
                      image={thumbnailUrl}
                      alt={label}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {creator}
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Chip
                          label={license}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                        <Tooltip title="Use Image">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSelectOpenverseImage(image);
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}

        {activeTab === 'wikimedia' && wikimediaImages.length > 0 && (
          <Box sx={{ height: 'calc(80vh - 210px)', overflowY: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 2,
              }}
            >
              {wikimediaImages.map((image) => {
                const thumbnailUrl = getWikimediaThumbnailUrl(image);
                const label = image.title?.trim() || 'Untitled';
                const artist = image.artist?.trim() || 'Unknown';
                const license = image.license?.toUpperCase() || 'CC';

                return (
                  <Card
                    key={image.pageId}
                    sx={{
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                      },
                    }}
                    onClick={() => handleSelectWikimediaImage(image)}
                  >
                    <CardMedia
                      component="img"
                      height="180"
                      image={thumbnailUrl}
                      alt={label}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {artist}
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Chip
                          label={license}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                        <Tooltip title="Use Image">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSelectWikimediaImage(image);
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}

        {activeTab === 'unsplash' && !unsplashLoading && photos.length === 0 && searchTerm && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <SearchIcon sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No images found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try different keywords
            </Typography>
          </Box>
        )}

        {activeTab === 'symbols' && !symbolsLoading && symbols.length === 0 && searchTerm && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <SearchIcon sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No symbols found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try a simpler vocab word or alternate term
            </Typography>
          </Box>
        )}

        {activeTab === 'arasaac' && !arasaacLoading && arasaacIcons.length === 0 && searchTerm && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <SearchIcon sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No ARASAAC pictograms found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try a simpler keyword, singular noun, or common verb
            </Typography>
          </Box>
        )}

        {activeTab === 'openmoji' &&
          !openMojiLoading &&
          openMojiIcons.length === 0 &&
          searchTerm && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <SearchIcon sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                No OpenMoji icons found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try broader terms like read, write, home, class, food
              </Typography>
            </Box>
          )}

        {activeTab === 'openverse' &&
          !openverseLoading &&
          openverseImages.length === 0 &&
          searchTerm && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <SearchIcon sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                No Openverse images found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try different keywords like ecosystem, volcano, solar system
              </Typography>
            </Box>
          )}

        {activeTab === 'wikimedia' &&
          !wikimediaLoading &&
          wikimediaImages.length === 0 &&
          searchTerm && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <SearchIcon sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                No Wikimedia images found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try different keywords like water cycle, civil war, periodic table
              </Typography>
            </Box>
          )}

        {activeTab === 'unsplash' && !unsplashLoading && photos.length === 0 && !searchTerm && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <SearchIcon sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              Search for classroom-ready images
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Example searches: ecosystem, historical map, fractions, microscope
            </Typography>
          </Box>
        )}

        {activeTab === 'symbols' && !symbolsLoading && symbols.length === 0 && !searchTerm && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <SearchIcon sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              Search for classroom symbols
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Example searches: eat, read, water, because, compare
            </Typography>
          </Box>
        )}

        {activeTab === 'arasaac' && !arasaacLoading && arasaacIcons.length === 0 && !searchTerm && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <SearchIcon sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              Search ARASAAC pictograms
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Example searches: read, eat, classroom, compare, listen
            </Typography>
          </Box>
        )}

        {activeTab === 'openmoji' &&
          !openMojiLoading &&
          openMojiIcons.length === 0 &&
          !searchTerm && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <SearchIcon sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                Search OpenMoji icons
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Example searches: book, school, pencil, clock, water
              </Typography>
            </Box>
          )}

        {activeTab === 'openverse' &&
          !openverseLoading &&
          openverseImages.length === 0 &&
          !searchTerm && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <SearchIcon sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                Search openly-licensed images
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Example searches: photosynthesis, water cycle, civil rights, fractions
              </Typography>
            </Box>
          )}

        {activeTab === 'wikimedia' &&
          !wikimediaLoading &&
          wikimediaImages.length === 0 &&
          !searchTerm && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <SearchIcon sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                Search Wikimedia Commons
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Example searches: water cycle, civil war, periodic table, DNA
              </Typography>
            </Box>
          )}

        {activeTab === 'upload' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              gap: 2,
            }}
          >
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                width: '100%',
                maxWidth: 480,
                border: '2px dashed',
                borderColor: isDragOver ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 6,
                textAlign: 'center',
                cursor: uploadLoading ? 'default' : 'pointer',
                bgcolor: isDragOver ? 'action.hover' : 'transparent',
                transition: 'border-color 0.2s, background-color 0.2s',
                pointerEvents: uploadLoading ? 'none' : 'auto',
              }}
            >
              {uploadLoading ? (
                <>
                  <CircularProgress size={48} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Uploading and resizing...
                  </Typography>
                </>
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="h6" color="text.secondary">
                    Drop an image here
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to browse your computer
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
                    JPEG, PNG, GIF, or WebP — max {PASTED_IMAGES_MAX_SIZE / (1024 * 1024)} MB
                  </Typography>
                </>
              )}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleFileUpload(file);
                event.target.value = '';
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          Powered by Unsplash, Global Symbols, ARASAAC, OpenMoji, Openverse, and Wikimedia Commons
        </Typography>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export type { UnsplashImageSelection };
export default UnsplashImageLibraryDialog;
